import React from 'react';
import { User, Phone, CalendarClock, Users as UsersIcon, ChevronRight, Activity, Mars, Venus } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';

/**
 * PatientCard — premium, scannable patient summary card used in the
 * reception queue, doctor's dashboard and family drawer. Click-through
 * actions:
 *   onOpen()      → primary tap (consultation or check-in)
 *   onFamily()    → open the family drawer for this patient
 *   onVitals()    → open the vitals history drawer (doctor only)
 *
 * The card pulls accent color from tenant branding so it feels
 * native to the clinic.
 */
export default function PatientCard({
  patient,
  onOpen,
  onFamily,
  onVitals,
  variant = 'reception', // 'reception' | 'doctor'
  badge,
}) {
  const { tenant } = useTenant();
  const brand = tenant?.branding?.primary_color || '#0f766e';
  const initials = (patient?.name || '?').trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const last = patient?.last_visit_at || patient?.last_consult_at;
  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
    } catch { return iso; }
  };

  const GenderIcon = (patient?.gender || '').toLowerCase() === 'female' ? Venus : Mars;
  const relationLabel = patient?.relationship && patient.relationship !== 'self' ? patient.relationship : null;

  return (
    <div
      className="group relative bg-white border rounded-2xl p-4 hover:shadow-md transition-all hover:-translate-y-0.5 hover:border-transparent ring-1 ring-zinc-100"
      data-testid={`patient-card-${patient?.id || 'x'}`}
      style={{ ['--brand']: brand }}
    >
      {/* Glow accent */}
      <div className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity"
           style={{ background: `linear-gradient(90deg, ${brand}, ${brand}66, transparent)` }} />

      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-extrabold text-base flex-shrink-0 shadow-sm"
             style={{ background: `linear-gradient(135deg, ${brand}, ${brand}cc)`, fontFamily: 'Manrope' }}>
          {initials || <User className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <button
              onClick={onOpen}
              className="font-bold text-zinc-900 text-[15px] leading-tight truncate hover:underline text-left"
              style={{ fontFamily: 'Manrope' }}
              data-testid="patient-card-name"
            >
              {patient?.name || '—'}
            </button>
            {patient?.is_primary === false && (
              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200">
                Family
              </span>
            )}
            {relationLabel && (
              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                {relationLabel}
              </span>
            )}
            {badge}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-1.5 flex-wrap">
            {patient?.age != null && <span className="inline-flex items-center gap-1"><GenderIcon className="h-3 w-3" />{patient.age}y</span>}
            {patient?.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{patient.phone}</span>}
            {last && (
              <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" />Last visit · <b className="text-zinc-700">{fmtDate(last)}</b></span>
            )}
            {!last && (
              <span className="text-zinc-400 italic">No visits yet</span>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-1.5 flex-wrap">
        <button
          onClick={onFamily}
          data-testid="patient-card-family"
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 px-2.5 py-1.5 rounded-md transition"
        >
          <UsersIcon className="h-3.5 w-3.5" /> Family
          <ChevronRight className="h-3 w-3 opacity-50" />
        </button>
        {variant === 'doctor' && (
          <button
            onClick={onVitals}
            data-testid="patient-card-vitals"
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 px-2.5 py-1.5 rounded-md transition"
          >
            <Activity className="h-3.5 w-3.5" /> Vitals history
            <ChevronRight className="h-3 w-3 opacity-50" />
          </button>
        )}
        {onOpen && (
          <button
            onClick={onOpen}
            data-testid="patient-card-open"
            className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold text-white px-3 py-1.5 rounded-md transition hover:shadow"
            style={{ background: brand }}
          >
            Open <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}
